import React, { useState, useEffect } from 'react';
import { TrendingUp, Plus, Edit2, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../../context/AuthContext';
import WeightChart from '../../components/WeightChart';
import Modal from '../../components/Modal';

const API = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  { key: 'waist', label: 'Waist', unit: 'in' },
  { key: 'hips', label: 'Hips', unit: 'in' },
  { key: 'thighs', label: 'Thighs', unit: 'in' },
  { key: 'chest', label: 'Chest', unit: 'in' },
  { key: 'biceps', label: 'Biceps', unit: 'in' },
  { key: 'forearms', label: 'Forearms', unit: 'in' },
];

const GOAL_TYPE_LABELS = { weight_loss: 'Weight Loss', weight_gain: 'Weight Gain', maintenance: 'Maintenance' };

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-lg text-sm font-body animate-fade-in-up
      ${type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
      {type === 'success' ? <CheckCircle className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
      {message}
    </div>
  );
}

const EMPTY_FORM = { date: new Date().toISOString().split('T')[0], weight: '', waist: '', hips: '', thighs: '', chest: '', biceps: '', forearms: '', notes: '' };

export default function StudentProgress() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  // Goal weight editing state
  const [showGoalEdit, setShowGoalEdit] = useState(false);
  const [goalInput, setGoalInput] = useState('');
  const [savingGoal, setSavingGoal] = useState(false);

  const fetchData = () => {
    setLoading(true);
    axios.get(`${API}/api/student/progress`, { headers: getAuthHeaders() })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  // Add measurement entry
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.weight) { setToast({ message: 'Weight is required.', type: 'error' }); return; }
    setSubmitting(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === '' ? null : v])
      );
      payload.weight = parseFloat(form.weight);
      await axios.post(`${API}/api/student/progress`, payload, { headers: getAuthHeaders() });
      setToast({ message: 'Entry added! Keep it up!', type: 'success' });
      setShowForm(false);
      setForm(EMPTY_FORM);
      fetchData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setToast({ message: typeof detail === 'string' ? detail : 'Something went wrong.', type: 'error' });
    } finally { setSubmitting(false); }
  };

  // Save goal weight
  const handleGoalSave = async () => {
    const val = parseFloat(goalInput);
    if (!goalInput || isNaN(val) || val <= 0) {
      setToast({ message: 'Please enter a valid positive number.', type: 'error' });
      return;
    }
    setSavingGoal(true);
    try {
      await axios.put(`${API}/api/student/goal`, { goal_weight: val }, { headers: getAuthHeaders() });
      setShowGoalEdit(false);
      setToast({ message: 'Goal weight updated!', type: 'success' });
      fetchData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setToast({ message: typeof detail === 'string' ? detail : 'Could not save. Try again.', type: 'error' });
    } finally { setSavingGoal(false); }
  };

  const entries = data?.entries || [];
  const latest = entries.length > 0 ? entries[entries.length - 1] : null;
  const goalWeight = data?.goal_weight;

  const kgRemaining = latest && goalWeight ? Math.abs(latest.weight - goalWeight).toFixed(1) : null;
  const isOnTarget = latest && goalWeight && latest.weight <= goalWeight;

  return (
    <div className="space-y-6 animate-fade-in-up">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[#F59E0B] font-body mb-1">My Journey</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#292524]">Progress</h1>
        </div>
        <button data-testid="add-my-entry-button" onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full px-5 py-2.5 text-sm font-heading font-medium shadow-button hover:-translate-y-0.5 transition-all">
          <Plus className="w-4 h-4" strokeWidth={2} /> Add Entry
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-10 h-10 border-4 border-[#F59E0B] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <>
          {/* ── Current Weight + Goal Weight ── */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-3xl p-5 shadow-card border border-[#E7E5E4]/50">
              <p className="text-xs text-[#A8A29E] font-body mb-1">Current Weight</p>
              <p className="font-heading text-3xl font-semibold text-[#F59E0B]">{latest ? `${latest.weight} kg` : '—'}</p>
              {data?.goal_type && <span className="text-xs bg-[#FFEDD5] text-[#D97706] rounded-full px-2.5 py-1 font-body inline-block mt-1">{GOAL_TYPE_LABELS[data.goal_type] || data.goal_type}</span>}
            </div>

            {/* Goal Weight — editable by student */}
            <div className="bg-white rounded-3xl p-5 shadow-card border border-[#E7E5E4]/50">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-[#A8A29E] font-body">Goal Weight</p>
                {!showGoalEdit && (
                  <button
                    data-testid="edit-goal-weight-btn"
                    onClick={() => { setGoalInput(goalWeight ? String(goalWeight) : ''); setShowGoalEdit(true); }}
                    title="Edit goal weight"
                    className="text-[#A8A29E] hover:text-[#F59E0B] transition-colors p-0.5"
                  >
                    <Edit2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                )}
              </div>

              {showGoalEdit ? (
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <input
                    data-testid="goal-weight-input"
                    type="number" step="0.1" min="0.1"
                    placeholder="kg"
                    value={goalInput}
                    onChange={e => setGoalInput(e.target.value)}
                    className="border-2 border-[#E7E5E4] rounded-xl px-3 py-1.5 text-sm outline-none w-20 focus:border-[#F59E0B] font-body"
                    autoFocus
                  />
                  <button
                    data-testid="save-goal-weight-btn"
                    onClick={handleGoalSave}
                    disabled={savingGoal}
                    className="bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-xl px-3 py-1.5 text-xs font-heading disabled:opacity-60 transition-colors"
                  >
                    {savingGoal ? '...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setShowGoalEdit(false)}
                    className="text-[#A8A29E] hover:text-[#292524] text-xs font-body transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <>
                  <p className="font-heading text-3xl font-semibold text-[#292524]">{goalWeight ? `${goalWeight} kg` : '—'}</p>
                  {kgRemaining && (
                    <p className={`text-xs font-body mt-1 ${isOnTarget ? 'text-green-600' : 'text-orange-500'}`}>
                      {isOnTarget ? `${kgRemaining} kg ahead!` : `${kgRemaining} kg remaining`}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* ── Weight Trend Chart ── */}
          <div className="bg-white rounded-3xl p-5 shadow-card border border-[#E7E5E4]/50">
            <h2 className="font-heading font-medium text-[#292524] mb-4">Weight Trend</h2>
            <WeightChart entries={entries} goalWeight={goalWeight} />
          </div>

          {/* ── Full Measurement History Table (same layout as Admin) ── */}
          {entries.length > 0 && (
            <div className="bg-white rounded-3xl shadow-card border border-[#E7E5E4]/50 overflow-hidden">
              <div className="px-5 py-4 border-b border-[#E7E5E4]/60">
                <h2 className="font-heading font-medium text-[#292524]">Measurement History</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#FFFAF0]">
                    <tr>
                      {['Date', 'Weight', 'Waist', 'Hips', 'Thighs', 'Chest', 'Biceps', 'Forearms'].map(h => (
                        <th key={h} className="text-left text-xs font-semibold text-[#78716C] uppercase tracking-wider px-4 py-3 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E7E5E4]/40">
                    {[...entries].reverse().map((e, i) => (
                      <tr key={e.id || i} data-testid={`student-progress-row-${i}`} className="hover:bg-[#FFFAF0]/60">
                        <td className="px-4 py-3 font-body text-[#292524] font-medium whitespace-nowrap">
                          {new Date(e.date + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="px-4 py-3 font-body font-semibold text-[#F59E0B]">{e.weight} kg</td>
                        {['waist', 'hips', 'thighs', 'chest', 'biceps', 'forearms'].map(f => (
                          <td key={f} className="px-4 py-3 font-body text-[#57534E]">{e[f] ? `${e[f]}"` : '—'}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {entries.length === 0 && (
            <div className="bg-white rounded-3xl p-10 text-center shadow-card border border-[#E7E5E4]/50">
              <TrendingUp className="w-10 h-10 text-[#F59E0B] mx-auto mb-3" strokeWidth={1.5} />
              <p className="font-heading font-medium text-[#292524]">No entries yet</p>
              <p className="text-[#78716C] text-sm font-body mt-1">Add your first measurement to start your journey!</p>
            </div>
          )}
        </>
      )}

      {/* ── Add Entry Modal ── */}
      {showForm && (
        <Modal
          title="Add Measurement"
          onClose={() => setShowForm(false)}
          footer={
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowForm(false)}
                className="flex-1 border-2 border-[#E7E5E4] text-[#57534E] rounded-full py-3 font-heading font-medium text-sm">Cancel</button>
              <button data-testid="submit-student-entry" type="submit" form="student-progress-form" disabled={submitting}
                className="flex-1 bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full py-3 font-heading font-medium text-sm shadow-button disabled:opacity-60 transition-all">
                {submitting ? 'Saving...' : 'Add Entry'}
              </button>
            </div>
          }
        >
          <form id="student-progress-form" onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-[#57534E] mb-1.5">Date</label>
                <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                  className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-3 py-2.5 focus:border-[#F59E0B] outline-none w-full text-sm font-body" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#57534E] mb-1.5">Weight (kg) *</label>
                <input data-testid="student-weight-input" type="number" step="0.1" placeholder="65.5" value={form.weight}
                  onChange={e => setForm(f => ({ ...f, weight: e.target.value }))}
                  className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-3 py-2.5 focus:border-[#F59E0B] outline-none w-full text-sm font-body" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {FIELDS.map(field => (
                <div key={field.key}>
                  <label className="block text-xs font-medium text-[#57534E] mb-1.5">{field.label} ({field.unit})</label>
                  <input type="number" step="0.1" placeholder="—" value={form[field.key]}
                    onChange={e => setForm(f => ({ ...f, [field.key]: e.target.value }))}
                    className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-3 py-2.5 focus:border-[#F59E0B] outline-none w-full text-sm font-body" />
                </div>
              ))}
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
